import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Edit, Info, MapPin, X } from '../ui/icons';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { venueBotMappingApi, VenueBotMapping } from '../../services/api';

function normalizeVenueValue(value: string): string {
  return value.trim().toLowerCase().split(/\s+/).filter(Boolean).join(' ');
}

function normalizeBotIdValue(value: string): string {
  return value.trim().toLowerCase();
}

function findVenueMappingConflict(
  venues: VenueBotMapping[],
  venue: string,
  botId: string,
  exclude?: VenueBotMapping
): 'pair' | 'venue' | 'bot_id' | null {
  const normalizedVenue = normalizeVenueValue(venue);
  const normalizedBotId = normalizeBotIdValue(botId);

  const conflict = venues.find((entry) => {
    if (exclude && entry.venue === exclude.venue && entry.bot_id === exclude.bot_id) {
      return false;
    }

    const entryVenue = normalizeVenueValue(entry.venue);
    const entryBotId = normalizeBotIdValue(entry.bot_id);

    if (entryVenue === normalizedVenue && entryBotId === normalizedBotId) {
      return 'pair';
    }

    if (entryVenue === normalizedVenue) {
      return 'venue';
    }

    if (entryBotId === normalizedBotId) {
      return 'bot_id';
    }

    return false;
  });

  if (!conflict) return null;

  const conflictVenue = normalizeVenueValue(conflict.venue);
  const conflictBotId = normalizeBotIdValue(conflict.bot_id);

  if (conflictVenue === normalizedVenue && conflictBotId === normalizedBotId) {
    return 'pair';
  }

  if (conflictVenue === normalizedVenue) {
    return 'venue';
  }

  return 'bot_id';
}

export default function VenuesPanel() {
  const [venues, setVenues] = useState<VenueBotMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newVenue, setNewVenue] = useState('');
  const [newBotId, setNewBotId] = useState('');

  const [editingVenue, setEditingVenue] = useState<VenueBotMapping | null>(null);
  const [editBotId, setEditBotId] = useState('');

  const loadVenues = async () => {
    setLoading(true);
    setError(null);

    const response = await venueBotMappingApi.listVenueBotMappings();
    if (response.success && response.data) {
      setVenues(response.data.venues || []);
    } else {
      setError(response.error || 'Failed to load venue mappings');
    }

    setLoading(false);
  };

  useEffect(() => {
    loadVenues();
  }, []);

  const filteredVenues = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return venues;
    return venues.filter(
      (v) => v.venue.toLowerCase().includes(query) || v.bot_id.toLowerCase().includes(query)
    );
  }, [venues, search]);

  const openCreateModal = () => {
    setNewVenue('');
    setNewBotId('');
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => setIsCreateModalOpen(false);

  const handleCreate = async () => {
    if (!newVenue.trim()) {
      toast.error('Venue name is required.');
      return;
    }
    if (!newBotId.trim()) {
      toast.error('Bot ID is required.');
      return;
    }

    const conflict = findVenueMappingConflict(venues, newVenue, newBotId);
    if (conflict === 'pair') {
      toast.error('A mapping with this venue and bot ID already exists.');
      return;
    }
    if (conflict === 'venue') {
      toast.error('This venue already has a mapping.');
      return;
    }
    if (conflict === 'bot_id') {
      toast.error('This bot ID is already assigned to another venue.');
      return;
    }

    setSaving(true);
    const response = await venueBotMappingApi.createVenueBotMapping({
      venue: newVenue.trim(),
      bot_id: newBotId.trim(),
    });
    setSaving(false);

    if (response.success) {
      toast.success('Venue mapping created');
      setIsCreateModalOpen(false);
      loadVenues();
    } else {
      toast.error(response.error || 'Failed to create venue mapping');
    }
  };

  const openEditModal = (venue: VenueBotMapping) => {
    setEditingVenue(venue);
    setEditBotId(venue.bot_id);
  };

  const closeEditModal = () => setEditingVenue(null);

  const handleUpdate = async () => {
    if (!editingVenue) return;
    if (!editBotId.trim()) {
      toast.error('Bot ID is required.');
      return;
    }

    const conflict = findVenueMappingConflict(venues, editingVenue.venue, editBotId, editingVenue);
    if (conflict === 'pair') {
      toast.error('A mapping with this venue and bot ID already exists.');
      return;
    }
    if (conflict === 'venue') {
      toast.error('This venue already has a mapping.');
      return;
    }
    if (conflict === 'bot_id') {
      toast.error('This bot ID is already assigned to another venue.');
      return;
    }

    setSaving(true);
    const response = await venueBotMappingApi.updateVenueBotMapping(editingVenue.venue, editingVenue.bot_id, {
      bot_id: editBotId.trim(),
    });
    setSaving(false);

    if (response.success) {
      toast.success('Venue mapping updated');
      setEditingVenue(null);
      loadVenues();
    } else {
      toast.error(response.error || 'Failed to update venue mapping');
    }
  };

  const handleDelete = async (venue: VenueBotMapping) => {
    const confirmDelete = window.confirm(
      `Delete the bot mapping for "${venue.venue}"? Games created at this venue will no longer auto-fill a bot ID.`
    );
    if (!confirmDelete) return;

    const response = await venueBotMappingApi.deleteVenueBotMapping(venue.venue, venue.bot_id);
    if (response.success) {
      toast.success('Venue mapping deleted');
      loadVenues();
    } else {
      toast.error(response.error || 'Failed to delete venue mapping');
    }
  };

  return (
    <div className="section seasons-section teams-section">
      {/* Constrained width: this is the first of what will grow into several small settings
          CRUD tables, so it stays compact rather than stretching the full page. Heading and
          "Add" button share one row, directly above the card, so they read as one component. */}
      <div className="seasons-columns settings-columns-narrow">
        <div className="settings-action-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="section-title seasons-section-title" style={{ margin: 0 }}>
            <MapPin size={24} />
            Venue Bot Mapping
          </h2>
          <button type="button" className="btn btn-small btn-primary" onClick={openCreateModal}>
            Add Venue Mapping
          </button>
        </div>

        <Card
          title="Venue List"
          description={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              Open any venue mapping to update its bot ID.
              <span
                title="Map each venue to the Genius bot ID that handles its games. Home games at the same venue share a bot ID, and the game-create form auto-fills from this table (with manual override)."
                style={{ cursor: 'help', opacity: 0.6, display: 'inline-flex', flexShrink: 0 }}
              >
                <Info size={14} />
              </span>
            </span>
          }
          headerAction={
            <div style={{ minWidth: 'min(100%, 220px)' }}>
              <Input label="" value={search} onChange={setSearch} placeholder="Search venue or bot ID" />
            </div>
          }
        >
          {error && (
            <div className="alert alert-danger" style={{ marginBottom: '16px' }}>
              {error}
            </div>
          )}

          <div className="settings-table-wrap">
            <table className="settings-table venues-table">
              <thead>
                <tr>
                  <th>Venue</th>
                  <th>Bot ID</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVenues.map((venue) => (
                  <tr key={venue.venue}>
                    <td>{venue.venue}</td>
                    <td>{venue.bot_id}</td>
                    <td>{venue.updated_at ? new Date(venue.updated_at).toLocaleString() : '-'}</td>
                    <td>
                      <div className="seasons-list-actions teams-list-actions">
                        <button type="button" className="seasons-view-button" onClick={() => openEditModal(venue)}>
                          Edit
                          <Edit size={14} />
                        </button>
                        <button
                          type="button"
                          className="list-delete-button"
                          onClick={() => handleDelete(venue)}
                          aria-label={`Delete venue mapping ${venue.venue}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {!loading && filteredVenues.length === 0 && (
                  <tr>
                    <td colSpan={4}>
                      <div className="seasons-footer-message">
                        No venue mappings found. Add one to get started.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {isCreateModalOpen && (
        <div className="seasons-modal-overlay" role="presentation" onClick={closeCreateModal}>
          <div
            className="seasons-modal-panel settings-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Add venue mapping"
            onClick={(e) => e.stopPropagation()}
          >
            <Card
              title="Add Venue Mapping"
              description="Map a venue to the bot ID that should handle its games."
              headerAction={
                <button
                  type="button"
                  className="seasons-modal-close"
                  onClick={closeCreateModal}
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Input
                  label="Venue"
                  value={newVenue}
                  onChange={setNewVenue}
                  placeholder="e.g., Cameron Indoor Stadium"
                  required
                />
                <Input label="Bot ID" value={newBotId} onChange={setNewBotId} placeholder="e.g., bot-12345" required />
              </div>

              <div
                className="button-group"
                style={{ marginTop: '18px', paddingTop: '16px', borderTop: '1px solid rgba(var(--color-border-rgb), 0.14)', justifyContent: 'flex-end' }}
              >
                <Button variant="outline" className="btn-small" onClick={closeCreateModal} disabled={saving}>
                  Cancel
                </Button>
                <Button variant="primary" className="btn-small" loading={saving} onClick={handleCreate}>
                  Save
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}

      {editingVenue && (
        <div className="seasons-modal-overlay" role="presentation" onClick={closeEditModal}>
          <div
            className="seasons-modal-panel settings-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Edit venue mapping"
            onClick={(e) => e.stopPropagation()}
          >
            <Card
              title="Edit Venue Mapping"
              description="The venue name can't be changed here — delete and re-add to rename it."
              headerAction={
                <button
                  type="button"
                  className="seasons-modal-close"
                  onClick={closeEditModal}
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Input label="Venue" value={editingVenue.venue} onChange={() => {}} disabled />
                <Input label="Bot ID" value={editBotId} onChange={setEditBotId} placeholder="e.g., bot-12345" required />
              </div>

              <div
                className="button-group"
                style={{ marginTop: '18px', paddingTop: '16px', borderTop: '1px solid rgba(var(--color-border-rgb), 0.14)', justifyContent: 'flex-end' }}
              >
                <Button variant="outline" className="btn-small" onClick={closeEditModal} disabled={saving}>
                  Cancel
                </Button>
                <Button variant="primary" className="btn-small" loading={saving} onClick={handleUpdate}>
                  Save
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
